/**
 * Department scope — WHO a person is scoped to, and WHAT an item is scoped to.
 *
 * Foundations 2 (Users rail), B2a-T1. principles.md #1 ("one store, one
 * resolver, one owner per noun") and #4 ("nothing fails open").
 *
 * THE RULE, stated once and implemented once
 *   docs/system/rails/users.md: "visibility = permission + a department in
 *   common". A person sees an item when they hold the permission AND either
 *   the item is company-wide, or the person is scoped to all departments, or
 *   the two department sets intersect. The ONE implementation is `canSee()` /
 *   `departmentVisibilitySql()` in `packages/api/src/policy/visibility.ts`,
 *   beside `can()`. No rail writes a department filter of its own.
 *
 * TWO SHAPES, NOT ONE, and the asymmetry is deliberate
 *   A PERSON is scoped by `DepartmentScope`: `allDepartments` true means "every
 *   department, including ones created tomorrow" — decisions.md C7, "no
 *   department today → all departments". An ITEM is scoped by
 *   `ItemDepartmentScope`: `companyWide` true means "no department restriction
 *   at all". They are different facts about different nouns and they are stored
 *   in different tables (`user_department_memberships` +
 *   `user_organizations.all_departments` for the person;
 *   `content_department_scopes` for the item, B2b-T1), so they
 *   get different types. Collapsing them into one `{ all, ids }` shape would
 *   let a call site pass a person where an item belongs and still type-check —
 *   and the two sides of the rule are not symmetric, so it would be wrong.
 *
 * BOTH HALVES OF THE PERSON'S SCOPE ARE PER-TENANT. `all_departments` is a
 * column on `user_organizations` — the membership row — and NOT on `users`
 * (review B2aT1-BF-01). A flag on the global user row would have let an admin
 * in one tenant widen that person's visibility in every other tenant they
 * belong to, which is the fail-open direction across a tenant boundary; the
 * membership half was already per-tenant, so half the scope would have been
 * isolated and half not.
 *
 * WHY ZOD, AND WHY HERE
 *   Zod is the type source (CLAUDE.md) and both shapes cross the package
 *   boundary: `DepartmentScope` rides on the Principal that `GET /users/me`
 *   projects, and `ItemDepartmentScope` is the wire shape the Library ACL
 *   surface serves for an item's departments (B2b-T1). Declared once, read by
 *   both sides.
 *
 * NEITHER SHAPE HAS A DEFAULT. A missing scope is not "all" and not "none": it
 * is "we could not find out", which every consumer must refuse rather than
 * guess at. `resolvePrincipal` throws `PrincipalUnavailableError` rather than
 * inventing one, and `canSee` denies an item whose scope it cannot read.
 */

import { z } from 'zod';

/**
 * The departments ONE PERSON is scoped to, as the principal carries it.
 *
 * `allDepartments` is the whole answer when it is true: `departmentIds` is then
 * informational (it is the membership rows the person also holds, if any) and
 * no consumer may narrow on it. When it is false the person sees only
 * company-wide items plus items sharing one of `departmentIds`.
 *
 * `allDepartments = false` with an EMPTY `departmentIds` is a legal, meaningful
 * value: "company-wide items only". It is the most restrictive scope there is,
 * and it is what a consumer falls back to when a scope cannot be read — never
 * `allDepartments = true`, which would be the fail-OPEN direction.
 */
export const DepartmentScopeSchema = z.object({
  /** True when this person is scoped to every department in the tenant. */
  allDepartments: z.boolean(),
  /** The department ids they hold membership of. Sorted, de-duplicated. */
  departmentIds: z.array(z.string().uuid()),
});
export type DepartmentScope = z.infer<typeof DepartmentScopeSchema>;

/**
 * The departments ONE ITEM is scoped to — a document, a folder, a workflow, an
 * agent: anything a rail shows a person.
 *
 * `companyWide` true means the item carries no department restriction, which is
 * how every item that has never been scoped behaves (B2b-T1's
 * `content_department_scopes` starts empty on purpose, so c29 changes nothing
 * visible on its own).
 *
 * `companyWide: false` WITH AN EMPTY `departmentIds` IS NOT A STORE STATE, and
 * that is the whole of what may be said about it (review B2aT1-BF-02). The
 * store records an item's departments as ROWS, so "restricted, but to nothing"
 * and "company-wide" are the same zero rows and the SQL form of the rule has no
 * rendering that tells them apart. The JS form DENIES the hand-built value —
 * fail-closed, principles.md #4 — which means the two forms would disagree
 * about an item that had been described wrongly rather than read. So a caller
 * never writes this shape by hand: `itemDepartmentScopeFromRows()` in
 * `packages/api/src/policy/visibility.ts` is the one way to derive it from the
 * rows, and it cannot produce the state at all.
 */
export const ItemDepartmentScopeSchema = z.object({
  /** True when the item is not restricted to any department. */
  companyWide: z.boolean(),
  /** The department ids the item is scoped to. */
  departmentIds: z.array(z.string().uuid()),
});
export type ItemDepartmentScope = z.infer<typeof ItemDepartmentScopeSchema>;

/**
 * The most departments one item may be scoped to in a single write.
 *
 * Not a business rule about how a tenant is organised — a ceiling, sized well
 * above any real org chart, that keeps an oversized request a 400 rather than a
 * 500 (review B2bT1-BF-06). See `ItemDepartmentScopeWriteSchema` below.
 */
export const MAX_ITEM_DEPARTMENT_IDS = 200;

/**
 * WHAT AN ADMIN MAY ASK FOR when they set an item's departments — the body of
 * `PUT /library/acl/documents/:id/departments` and its folder twin (B2b-T1).
 *
 * The read shape above DESCRIBES a state; this one is a REQUEST, and the two
 * are not the same set of values. The store cannot hold "restricted, but to
 * nothing" — an item's departments are rows, and zero rows is company-wide — so
 * `companyWide: false` with an empty list is not a scope the admin can be given
 * and it is refused HERE, at the door, rather than silently stored as its
 * opposite.
 *
 * REFUSED, NOT COERCED, and the direction is the reason. Coercing it to
 * company-wide would widen an item the admin was trying to narrow; coercing it
 * to "no departments" would hide an item from everyone including its owner.
 * Both are answers to a question the admin did not ask. The same refusal shape
 * B2b-T3 gives a PERSON patched to zero departments with `all: false`: a scope
 * that shows nothing is never what an admin means, so it is a 400 rather than a
 * quiet blind spot (principles.md #4 — nothing fails open, and nothing fails
 * silently shut either).
 *
 * `companyWide: true` WITH ids present is accepted and the ids are ignored,
 * which is the same asymmetry `DepartmentScope.allDepartments` has: the flag is
 * the whole answer, and refusing the combination would mean an admin could not
 * tick "company-wide" without first clearing the boxes below it.
 */
export const ItemDepartmentScopeWriteSchema = ItemDepartmentScopeSchema.extend({
  // BOUNDED AT THE DOOR (review B2bT1-BF-06). Unbounded, this array is the
  // shortest path from a 1 MB request body to a 500: the route echoes every
  // unknown id back in a 422 message, and the repository's multi-row INSERT
  // spends five bind parameters per id against postgres's 65,535-parameter
  // protocol limit — so ~13k ids turn a bad request into a server error and
  // ~25k turn it into a megabyte of error text. A tenant has departments, not
  // department ids by the tens of thousands, so the realistic count is the
  // bound and anything past it is a 400 before a query runs.
  departmentIds: z.array(z.string().uuid()).max(MAX_ITEM_DEPARTMENT_IDS, {
    message: `An item may be scoped to at most ${MAX_ITEM_DEPARTMENT_IDS} departments.`,
  }),
}).refine(
  (value) => value.companyWide || value.departmentIds.length > 0,
  {
    message:
      'An item must be company-wide or scoped to at least one department. ' +
      '"Not company-wide, and no departments" is a scope nobody can see, which is never what an admin means.',
    path: ['departmentIds'],
  },
);
export type ItemDepartmentScopeWrite = z.infer<typeof ItemDepartmentScopeWriteSchema>;

/**
 * The most departments ONE PERSON may be put in by a single write.
 *
 * The same ceiling, for the same reason, `MAX_ITEM_DEPARTMENT_IDS` carries: an
 * unbounded array is the shortest path from a large request body to a 500,
 * because the route echoes unknown ids back in its 422 and the repository binds
 * one parameter per id. A person belongs to departments, not to tens of
 * thousands of them, so past this it is a 400 before a query runs.
 */
export const MAX_USER_DEPARTMENT_IDS = 200;

/**
 * THE DEPARTMENTS ONE PERSON IS IN, AS THE USERS RAIL RENDERS THEM — the wire
 * shape of `departments` on every user the API returns (B2b-T3).
 *
 * WHY IT IS NOT `DepartmentScope`. That shape is the PRINCIPAL's, and it
 * carries ids only because the visibility rule never renders anything: an
 * intersection test does not need a name. A roster cell, a detail-page field
 * and a wizard summary all print names, and the alternative — the browser
 * joining ids against its own copy of the department list — is the second
 * reader this whole rail exists to remove (a name it could not find would
 * render as a uuid, which is how a stale local list gets believed).
 *
 * `all` IS THE WHOLE ANSWER WHEN TRUE, and `items` is then empty: an
 * all-departments person holds no membership rows (decisions.md C7, "no
 * department today → all departments"), so the store cannot produce both. A
 * screen renders "All departments" for `all` and the list otherwise, and
 * `all: false` with an empty `items` is the honest rendering of a person the
 * store holds nothing for — company-wide items only, never "everything".
 */
export const UserDepartmentsSchema = z.object({
  /** True when this person is scoped to every department in the tenant. */
  all: z.boolean(),
  /**
   * The departments they are in, oldest membership first — the order
   * `resolveDepartmentScopes` (packages/api/src/policy/principal.ts) reads them
   * in, so two screens showing the same person list them the same way.
   */
  items: z.array(z.object({ id: z.string().uuid(), name: z.string() })),
});
export type UserDepartments = z.infer<typeof UserDepartmentsSchema>;

/**
 * WHAT AN ADMIN MAY ASK FOR when they set a person's departments — the
 * `departments` key on `POST /users` and `PATCH /users/:id` (B2b-T3).
 *
 * A REQUEST, NOT A STATE, exactly as `ItemDepartmentScopeWriteSchema` is for an
 * item, and the same one value is refused here as there: `all: false` with an
 * empty list. For a person it means "holds the permission, shares a department
 * with nothing" — they would see company-wide items and nothing else, which is
 * never what an admin picked "not all departments" to say. It is a 400 at the
 * door rather than a blind spot discovered weeks later (principles.md #4).
 *
 * `all: true` WITH ids IS ACCEPTED AND THE ids ARE IGNORED. The two halves of a
 * person's scope are one fact — `user_organizations.all_departments` is true
 * exactly when the person holds no membership rows — so "all departments, and
 * also these three" is not a state the store can hold. Refusing the combination
 * instead would mean an admin could not tick "All departments" without first
 * clearing the boxes underneath it.
 */
export const UserDepartmentsWriteSchema = z
  .object({
    /** Scope this person to every department in the tenant. */
    all: z.boolean(),
    /**
     * The departments they are in. Ignored when `all` is true.
     *
     * OPTIONAL, DEFAULTING TO EMPTY (review BF-B2bT3-07). Three places document
     * the widening body as `{ all: true }` with no ids — the route comment on
     * PATCH /users/:id, `use-users.ts`, and this file's own prose above — and a
     * required key made every one of them a 422 that aborted the whole PATCH,
     * including the fields beside it. The default is the empty list, which is
     * exactly what `userDepartmentIdsFromWrite` reads an `all: true` body as,
     * so the documented body and the accepted body are now the same body. It
     * does NOT weaken the refusal below: `{ all: false }` with no ids still
     * fails the refine, the same as `{ all: false, ids: [] }` spelled out.
     */
    ids: z
      .array(z.string().uuid())
      .max(MAX_USER_DEPARTMENT_IDS, {
        message: `A person may be put in at most ${MAX_USER_DEPARTMENT_IDS} departments.`,
      })
      .default([]),
  })
  .refine((value) => value.all || value.ids.length > 0, {
    message:
      'A person must be in all departments or in at least one department. ' +
      '"Not all departments, and no departments" is a scope that shows nothing, which is never what an admin means.',
    path: ['ids'],
  });
export type UserDepartmentsWrite = z.infer<typeof UserDepartmentsWriteSchema>;

/**
 * The department ids a write means, as the ONE store writer takes them.
 *
 * `setUserDepartments` (packages/api/src/repositories/user-department.repository.ts)
 * takes a list and derives the flag from it — empty means all-departments — so
 * "all" reaches it as the empty list rather than as a second parameter that
 * could disagree with the first. Declared here so the API and any other caller
 * translate the request the same way.
 */
export function userDepartmentIdsFromWrite(
  value: UserDepartmentsWrite,
): readonly string[] {
  return value.all ? [] : [...new Set(value.ids)].sort();
}

// ─── The "Departments" CSV cell — ONE translation, both directions ─────────
//
// Review BF-B2bT3-01 / B2bT3-RV-01. The roster export wrote the words
// "All departments" for a decisions.md C7 person and the import read the same
// column as a list of department NAMES, so every default-scoped row in an
// exported file came back as `UNKNOWN_DEPARTMENT: Department not found in this
// tenant: 'All departments'` — naming a department the operator never typed,
// on the majority of the file. The two directions were two private rules in
// one route file; they are one rule here, and `users-csv-department-cell.test.ts`
// runs the round trip.

/** The separator between two department names in one exported cell. */
export const DEPARTMENT_CSV_SEPARATOR = '; ';

/**
 * The cell an ALL-DEPARTMENTS person exports as, and the cell the import reads
 * back as that same person.
 *
 * WORDS AND NOT AN EMPTY CELL, for the reason the export gives: "no departments
 * recorded" and "every department in the tenant" are opposite facts and an
 * auditor reading the file cannot tell them apart otherwise.
 */
export const ALL_DEPARTMENTS_CSV_CELL = 'All departments';

/** The lookup key `ALL_DEPARTMENTS_CSV_CELL` would have as a department name. */
export const ALL_DEPARTMENTS_CSV_CELL_KEY = ALL_DEPARTMENTS_CSV_CELL.toLowerCase();

/**
 * WHAT A DEPARTMENT MAY BE CALLED (review BF-B2bT3-02).
 *
 * Two names are refused, and both refusals exist because the name is what the
 * CSV round trip carries:
 *
 *   - A name containing `;` can never be typed back in. The cell separator is
 *     the semicolon in both directions, so `Ops; East` exports as one cell and
 *     imports as the two unknown names `Ops` and `East` — the department
 *     becomes permanently unreachable from the import path, with no escaping
 *     anywhere to rescue it. Refusing the character at the door is the only
 *     place the operator can still fix it.
 *   - `All departments` is the sentinel above. A real department by that name
 *     and the all-departments scope would be the same cell, and a cell that
 *     means two things is a scope decided by a coin toss.
 *
 * Names created BEFORE this rule are not rewritten — this is a write-side
 * refusal, and the import prefers a real department of that name over the
 * sentinel so an existing one still resolves.
 */
export const DepartmentNameSchema = z
  .string()
  .min(1)
  .max(200)
  .trim()
  .refine((name) => !name.includes(';'), {
    message:
      'A department name may not contain a semicolon: the semicolon separates ' +
      'department names in the Users CSV import and export, so such a name could ' +
      'never be imported back.',
  })
  .refine((name) => name.trim().toLowerCase() !== ALL_DEPARTMENTS_CSV_CELL_KEY, {
    message:
      `'${ALL_DEPARTMENTS_CSV_CELL}' is reserved: it is the CSV cell that means ` +
      'a person is in every department, so a department by that name could never ' +
      'be told apart from that scope on import.',
  });

/**
 * One person's departments as one CSV cell — the EXPORT direction.
 *
 * @param scope  `allDepartments` plus the names of the departments held.
 * @returns the cell, or `null` for a person the store holds nothing for (no
 *          flag and no rows) — which `csvEscape` renders as the empty cell.
 */
export function formatDepartmentsCsvCell(scope: {
  allDepartments: boolean;
  names: readonly string[];
}): string | null {
  if (scope.allDepartments) return ALL_DEPARTMENTS_CSV_CELL;
  if (scope.names.length === 0) return null;
  return scope.names.join(DEPARTMENT_CSV_SEPARATOR);
}

/**
 * One CSV cell as the names it holds — the IMPORT direction.
 *
 * `allDepartments` is true when the whole cell is the sentinel the export
 * writes (trimmed, case-insensitively). `names` is returned ALONGSIDE it rather
 * than emptied, so the caller can prefer a real department of that name — one
 * created before `DepartmentNameSchema` reserved it — over the sentinel
 * reading, and only fall back to "all departments" when the tenant has no such
 * department.
 */
export function parseDepartmentsCsvCell(raw: string): {
  allDepartments: boolean;
  names: string[];
} {
  const names = raw
    .split(';')
    .map((part) => part.trim())
    .filter((part) => part.length > 0);
  const allDepartments =
    names.length === 1 && names[0]!.toLowerCase() === ALL_DEPARTMENTS_CSV_CELL_KEY;
  return { allDepartments, names };
}

/**
 * WHAT MAY BE DEPARTMENT-SCOPED — the `content_department_scopes.entity_kind`
 * vocabulary, as KEYS (principles.md #5, "reference by key, never by name, slug
 * or path prefix").
 *
 * WHY IT IS A CLOSED SET AND NOT A FREE-FORM STRING (review B2aT1-R1). The SQL
 * form of the rule looks an item up by `(organization_id, entity_kind,
 * entity_id)`. A kind that does not match what the repository WROTE finds no
 * rows, and no rows means company-wide — so a single typo (`'workflows'` for
 * `'workflow'`) makes every department-scoped item of that kind visible to
 * everyone holding the permission, silently, in the one function that owns the
 * rule. That is the fail-OPEN direction, so the kind is a key from this list
 * and `departmentVisibilitySql` denies anything else.
 *
 * DECLARED ONCE, HERE. B2b-T1's migration c29 generates its `entity_kind` CHECK
 * from this list and `content-department-scope.repository.ts` writes only these
 * values, so the predicate and the store cannot drift onto different
 * vocabularies. Adding a kind is one line in one file plus the CHECK.
 *
 * NOT `entity-kinds.ts`. That file is the BUNDLE wire vocabulary — what an
 * import/export archive may carry (ADR-0014 CRIT-3) — a different noun with a
 * different owner and a different reason to change. Sharing one enum between
 * them would mean a bundle format change silently widening what can be
 * department-scoped.
 */
export const ContentDepartmentScopeEntityKindSchema = z.enum([
  /** Library: one document. */
  'document',
  /**
   * Library: a folder — and, by inheritance, the documents beneath it.
   *
   * The founder decided it (decision 23, B4, 2026-10-21): a document's
   * departments are its OWN rows if it has any, otherwise the rows of the
   * NEAREST folder above it that has any (walking up), otherwise company-wide.
   * Decided once, in the Document visibility resolver
   * (api policy/document-visibility.ts `departmentInheritance`); no reader
   * applies a folder's rows to a document itself. It holds on a surface once
   * that surface's reader asks the resolver (Library LB-4); a reader still on
   * the older document-acl path reads a document's own rows only.
   */
  'folder',
  /** Automate: one workflow definition. */
  'workflow',
  /** Agents: one agent. */
  'agent',
]);
export type ContentDepartmentScopeEntityKind = z.infer<
  typeof ContentDepartmentScopeEntityKindSchema
>;

/** The same list as a plain array, for a CHECK generator or a runtime test. */
export const CONTENT_DEPARTMENT_SCOPE_ENTITY_KINDS: readonly ContentDepartmentScopeEntityKind[] =
  Object.freeze([...ContentDepartmentScopeEntityKindSchema.options]);

/**
 * Is this a kind the department scope store may hold? Fails closed on anything
 * else — a non-string, an empty string, an unknown key.
 */
export function isContentDepartmentScopeEntityKind(
  value: unknown,
): value is ContentDepartmentScopeEntityKind {
  return ContentDepartmentScopeEntityKindSchema.safeParse(value).success;
}

/**
 * The most restrictive person scope there is: company-wide items only.
 *
 * Exported so that the one place a consumer needs a fallback uses THE value
 * rather than writing `{ allDepartments: false, departmentIds: [] }` inline and
 * inviting the next reader to "simplify" it to `true`.
 */
export const DEPARTMENT_SCOPE_NONE: DepartmentScope = Object.freeze({
  allDepartments: false,
  departmentIds: Object.freeze([]) as unknown as string[],
});
